import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, ArrowRight, PackagePlus, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/api/client";
import { AuthImage } from "@/components/AuthImage";
import { money, DELIVERY_OPTIONS } from "@/lib/pricing";
import { getTheme } from "@/lib/theme";

export default function LargeOrder() {
  const { selectedBusinessId, user } = useAuth();
  const nav = useNavigate();
  const theme = getTheme(selectedBusinessId);

  const [products, setProducts] = useState([]);
  const [qtys, setQtys] = useState({});
  const [search, setSearch] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [fulfillment, setFulfillment] = useState("pickup");
  const [address, setAddress] = useState("");
  const [description, setDescription] = useState("");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user]);

  useEffect(() => {
    if (!selectedBusinessId) return;
    api.listProducts(selectedBusinessId).then(setProducts).catch(() => {});
  }, [selectedBusinessId]);

  const setQty = (id, q) =>
    setQtys((m) => {
      const next = { ...m, [id]: Math.max(0, q) };
      if (next[id] === 0) delete next[id];
      return next;
    });

  const selectedCount = useMemo(
    () => Object.values(qtys).reduce((a, b) => a + b, 0),
    [qtys]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => p.name.toLowerCase().includes(q));
  }, [products, search]);

  const submit = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Please enter your name and phone");
      return;
    }
    if (!date) {
      toast.error("Please choose an event / pickup date");
      return;
    }
    if (fulfillment !== "pickup" && !address.trim()) {
      toast.error("Delivery address is required");
      return;
    }
    if (selectedCount === 0 && !description.trim()) {
      toast.error("Add some items or describe your large order");
      return;
    }

    const chosen = products.filter((p) => qtys[p.id] > 0);
    const items =
      chosen.length > 0
        ? chosen.map((p) => ({
            product_id: p.id,
            product_name: p.name,
            quantity: qtys[p.id],
            unit_price: 0,
            subtotal: 0,
          }))
        : [
            {
              product_id: "large-order",
              product_name: "Custom large order",
              quantity: 1,
              unit_price: 0,
              subtotal: 0,
            },
          ];

    let pickup_at = null;
    try {
      pickup_at = new Date(`${date}T${time || "12:00"}`).toISOString();
    } catch (e) {
      /* invalid date — send null */
    }

    setPlacing(true);
    try {
      const order = await api.createOrder({
        business_id: selectedBusinessId,
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        pickup_date: date + (time ? ` ${time}` : ""),
        pickup_at,
        notes: description.trim() || null,
        fulfillment_type: fulfillment,
        delivery_address: fulfillment !== "pickup" ? address.trim() : null,
        is_large_order: true,
        items,
      });
      toast.success("Large order request sent! The kitchen will send you a price.");
      nav(`/order/${order.id}`);
    } catch (err) {
      toast.error(err.message || "Could not send your request");
    } finally {
      setPlacing(false);
    }
  };

  const inputStyle = { background: "var(--surface)", border: "1px solid var(--border)" };

  return (
    <div className="container-app py-8 sm:py-12">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{ background: "var(--accent-soft)" }}>
          <PackagePlus size={22} style={{ color: "var(--brand)" }} />
        </div>
        <div>
          <h1 className="font-display text-4xl" style={{ color: "var(--ink)" }}>Large / bulk order</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--muted)" }}>
            Planning a big event? Build your order in one deal — {theme.name} reviews it and sends you a single quoted price.
          </p>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-[1fr_380px] gap-8 items-start">
        {/* Item picker + description */}
        <div className="space-y-5">
          <div className="rounded-2xl p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-display text-xl" style={{ color: "var(--ink)" }}>Pick items &amp; quantities</h2>
              {selectedCount > 0 && (
                <span data-testid="large-order-selected-count" className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: "var(--accent-soft)", color: "var(--brand)" }}>
                  {selectedCount} item{selectedCount > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-xl px-3 py-2" style={inputStyle}>
              <Search size={16} style={{ color: "var(--muted)" }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search the menu"
                data-testid="large-order-search"
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </div>

            <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {filtered.length === 0 ? (
                <p className="text-sm py-6 text-center" style={{ color: "var(--muted)" }}>No items match your search.</p>
              ) : (
                filtered.map((p) => {
                  const qty = qtys[p.id] || 0;
                  return (
                    <div key={p.id} data-testid={`large-order-product-${p.id}`} className="flex items-center gap-3 rounded-xl p-2.5" style={{ background: qty ? "var(--accent-soft)" : "var(--surface-2)" }}>
                      <AuthImage src={p.image_url} alt={p.name} className="h-14 w-14 rounded-lg object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate" style={{ color: "var(--ink)" }}>{p.name}</div>
                        <div className="text-xs capitalize" style={{ color: "var(--muted)" }}>{p.category}</div>
                      </div>
                      {qty === 0 ? (
                        <button
                          onClick={() => setQty(p.id, 1)}
                          data-testid={`large-order-add-${p.id}`}
                          className="rounded-full px-3.5 py-1.5 text-sm font-semibold inline-flex items-center gap-1"
                          style={{ border: "1px solid var(--border)", color: "var(--brand)" }}
                        >
                          <Plus size={14} /> Add
                        </button>
                      ) : (
                        <div className="flex items-center rounded-full" style={{ border: "1px solid var(--border)", background: "var(--surface)" }}>
                          <button onClick={() => setQty(p.id, qty - 1)} data-testid={`large-order-dec-${p.id}`} className="px-2.5 py-1.5" aria-label="Decrease"><Minus size={14} /></button>
                          <span className="w-9 text-center text-sm font-semibold" data-testid={`large-order-qty-${p.id}`}>{qty}</span>
                          <button onClick={() => setQty(p.id, qty + 1)} data-testid={`large-order-inc-${p.id}`} className="px-2.5 py-1.5" aria-label="Increase"><Plus size={14} /></button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="rounded-2xl p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
            <h2 className="font-display text-xl mb-2" style={{ color: "var(--ink)" }}>Tell us about the order</h2>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. 3 dozen assorted cupcakes + a 2-tier cake for 60 guests, chocolate & vanilla, delivered by 2pm. Mention theme, flavors, dietary needs, headcount…"
              data-testid="large-order-description"
              rows={5}
              className="w-full rounded-xl px-4 py-3 outline-none resize-none text-sm"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Contact + submit */}
        <div className="rounded-2xl p-6 sticky top-20" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <h2 className="font-display text-2xl mb-4" style={{ color: "var(--ink)" }}>Your details</h2>
          <div className="space-y-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="large-order-name" className="w-full rounded-xl px-4 py-2.5 outline-none" style={inputStyle} />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" data-testid="large-order-phone" className="w-full rounded-xl px-4 py-2.5 outline-none" style={inputStyle} />
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="large-order-date" className="w-full rounded-xl px-3 py-2.5 outline-none" style={inputStyle} />
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="large-order-time" className="w-full rounded-xl px-3 py-2.5 outline-none" style={inputStyle} />
            </div>
          </div>

          <div className="mt-4">
            <div className="text-sm font-semibold mb-2" style={{ color: "var(--ink)" }}>Fulfillment</div>
            <div className="space-y-2">
              {DELIVERY_OPTIONS.map((d) => (
                <button
                  key={d.key}
                  onClick={() => setFulfillment(d.key)}
                  data-testid={`large-order-fulfillment-${d.key}`}
                  className="w-full flex items-center justify-between rounded-xl px-4 py-2.5 text-left transition"
                  style={fulfillment === d.key ? { border: "2px solid var(--brand)", background: "var(--accent-soft)" } : { border: "1px solid var(--border)" }}
                >
                  <div>
                    <div className="text-sm font-semibold" style={{ color: "var(--ink)" }}>{d.label}</div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>{d.desc}</div>
                  </div>
                </button>
              ))}
            </div>
            {fulfillment !== "pickup" && (
              <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Delivery address" data-testid="large-order-address" className="w-full mt-2 rounded-xl px-4 py-2.5 outline-none" style={inputStyle} />
            )}
          </div>

          <div className="mt-4 rounded-xl p-3 text-xs" style={{ background: "var(--surface-2)", color: "var(--muted)" }}>
            No payment now. {theme.name} reviews your request and sends one total price — then you pay a deposit to lock it in.
          </div>

          <button onClick={submit} disabled={placing} data-testid="large-order-submit-btn" className="btn-brand w-full mt-5 rounded-xl py-3.5 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {placing ? "Sending…" : "Send large order request"} {!placing && <ArrowRight size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}
